
import Vue from "vue";
import { mapActions, mapGetters } from "vuex";
import {
  GET_USER_DETAILS,
  USER_DETAILS_BY_ID
} from "@/store/modules/auth/constants";
import AiReportMetricCard from "@/components/candidate/interviews/ai/report/AiReportMetricCard.vue";
import AiReportInterviewDetailsCard from "@/components/candidate/interviews/ai/report/AiReportInterviewDetailsCard.vue";
import AiReportInsightsCard from "@/components/candidate/interviews/ai/report/AiReportInsightsCard.vue";
import {
  generate_audit,
  generate_final_report,
  get_latest_interview_report,
  upsert_interview_report
} from "@/store/apis/ml_interview";
import { fetch_user_with_profile, get_job_by_id } from "@/store/apis/common";
import {
  AI_INTERVIEW_INVITE_BY_ID,
  INIT_AI_INTERVIEW_INVITES
} from "@/store/modules/recruiter_ai_interviews/constants";

export default Vue.extend({
  name: "RecruiterPersonInterviewReport",
  components: {
    AiReportMetricCard,
    AiReportInterviewDetailsCard,
    AiReportInsightsCard
  },
  data() {
    return {
      reportRecord: null as any,
      candidateUser: null as any,
      loading: false,
      generatingFinalReport: false,
      botAvatar: require("@/assets/images/female-bot.png")
    };
  },
  computed: {
    ...mapGetters("auth", {
      user: GET_USER_DETAILS
    }),
    ...mapGetters("recruiter_ai_interviews", {
      inviteById: AI_INTERVIEW_INVITE_BY_ID
    }),
    routeCandidateId(): number {
      return Number(this.$route.query.candidateId || 0);
    },
    routeJobId(): number {
      return Number(this.$route.query.jobId || 0);
    },
    inviteId(): string {
      return String(this.$route.query.inviteId || "");
    },
    invite(): any {
      return this.inviteId ? this.inviteById(this.inviteId) : null;
    },
    resolvedCandidateId(): number {
      return Number(this.routeCandidateId || this.invite?.candidate_id || 0);
    },
    candidateName(): string {
      const candidateFromUser = `${this.candidateUser?.first_name || ""} ${
        this.candidateUser?.last_name || ""
      }`.trim();
      if (candidateFromUser) return candidateFromUser;
      const inviteName = String(this.invite?.candidate_name || "").trim();
      if (inviteName) return inviteName;
      return "Interview Candidate";
    },
    candidateAvatar(): string {
      return (
        this.candidateUser?.avatar_uri ||
        this.invite?.avatar_uri ||
        this.user?.avatar_uri ||
        this.botAvatar
      );
    },
    auditReport(): any {
      return this.reportRecord?.audit_report || {};
    },
    finalReport(): any {
      const raw = this.reportRecord?.final_report;
      if (!raw) return {};
      if (typeof raw === "object") return raw;
      if (typeof raw === "string") {
        try {
          return JSON.parse(raw);
        } catch (_e) {
          return {};
        }
      }
      return {};
    },
    summaryText(): string {
      return this.auditReport?.summary || "Audit report is not available.";
    },
    overallScore(): string {
      return this.auditReport?.overall_score
        ? String(this.auditReport.overall_score)
        : "-";
    },
    verdict(): string {
      return this.auditReport?.verdict || "-";
    },
    conductScores(): Record<string, any> {
      return this.auditReport?.conduct_scores || {};
    },
    biasFlags(): any[] {
      return this.auditReport?.bias_flags || [];
    },
    coaching(): string[] {
      const items = Array.isArray(this.auditReport?.coaching_recommendations)
        ? this.auditReport.coaching_recommendations
        : [];
      return items
        .map((item: any) =>
          typeof item === "string"
            ? item
            : String(item?.area || item?.recommendation || "").trim()
        )
        .filter((v: string) => !!v);
    },
    interviewStatsRows(): Array<{ label: string; value: string }> {
      const stats = this.auditReport?.interview_stats || {};
      const rows = [
        {
          label: "Candidate speaking time",
          value: `${Number(stats?.estimated_candidate_speaking_time_percent || 0)}%`
        },
        {
          label: "Interviewer speaking time",
          value: `${Number(stats?.estimated_interviewer_speaking_time_percent || 0)}%`
        },
        {
          label: "Total questions asked",
          value: String(Number(stats?.total_questions_asked || 0))
        },
        {
          label: "Questions from brief used",
          value: String(Number(stats?.questions_from_brief_used || 0))
        },
        {
          label: "Follow-up questions asked",
          value: String(Number(stats?.follow_up_questions_asked || 0))
        },
        {
          label: "Sections fully covered",
          value: String(Number(stats?.sections_fully_covered || 0))
        },
        {
          label: "Sections skipped",
          value: String(Number(stats?.sections_skipped || 0))
        }
      ];
      return rows;
    },
    sectionCoverageList(): any[] {
      return Array.isArray(this.auditReport?.section_coverage)
        ? this.auditReport.section_coverage
        : [];
    },
    questionsSkippedList(): any[] {
      return Array.isArray(this.auditReport?.questions_coverage?.skipped)
        ? this.auditReport.questions_coverage.skipped
        : [];
    },
    questionsModifiedList(): any[] {
      return Array.isArray(this.auditReport?.questions_coverage?.modified)
        ? this.auditReport.questions_coverage.modified
        : [];
    },
    criticalProbesFollowthroughList(): any[] {
      return Array.isArray(this.auditReport?.critical_probes_followthrough)
        ? this.auditReport.critical_probes_followthrough
        : [];
    },
    questionQualityAnalysisList(): any[] {
      return Array.isArray(this.auditReport?.question_quality_analysis)
        ? this.auditReport.question_quality_analysis
        : [];
    },
    interviewerStrengthsList(): string[] {
      return Array.isArray(this.auditReport?.interviewer_strengths)
        ? this.auditReport.interviewer_strengths
            .map((item: any) => String(item || "").trim())
            .filter((item: string) => !!item)
        : [];
    },
    coachingRecommendationsList(): any[] {
      return Array.isArray(this.auditReport?.coaching_recommendations)
        ? this.auditReport.coaching_recommendations
        : [];
    },
    hasDetailedCoachingPlan(): boolean {
      const plan = this.auditReport?.detailed_coaching_plan || {};
      return Object.keys(plan).length > 0;
    },
    detailedCoachingImmediateActions(): string[] {
      const items = this.auditReport?.detailed_coaching_plan?.immediate_actions;
      return Array.isArray(items)
        ? items.map((item: any) => String(item || "").trim()).filter((item: string) => !!item)
        : [];
    },
    detailedCoachingShortTerm(): string[] {
      const items = this.auditReport?.detailed_coaching_plan?.short_term_development;
      return Array.isArray(items)
        ? items.map((item: any) => String(item || "").trim()).filter((item: string) => !!item)
        : [];
    },
    detailedCoachingTrainingRecommended(): string {
      return String(this.auditReport?.detailed_coaching_plan?.training_recommended || "no");
    },
    detailedCoachingTrainingFocus(): string {
      return String(this.auditReport?.detailed_coaching_plan?.training_focus || "");
    },
    detailsTop(): { label: string; value: string }[] {
      const brief = this.reportRecord?.interviewer_brief || {};
      const experience = String(
        brief?.experience_level ||
          brief?.candidate_experience_level ||
          this.invite?.candidate_experience_level ||
          ""
      ).trim();
      return [
        {
          label: "Role",
          value:
            String(
              this.invite?.job_role ||
                this.invite?.candidate_role ||
                this.reportRecord?.job_role ||
                ""
            ).trim() || "Candidate"
        },
        { label: "Candidate", value: this.candidateName },
        { label: "Experience Level", value: experience || "N/A" }
      ];
    },
    detailsBottom(): { label: string; value: string }[] {
      const durationMinutes = Number(
        this.invite?.duration_minutes || this.reportRecord?.duration_minutes || 0
      );
      const scheduledValue = this.formatScheduleValue(
        this.invite?.scheduled_at || this.reportRecord?.scheduled_at || ""
      );
      const hasTeamsLink =
        !!this.invite?.ms_teams_link ||
        !!this.invite?.ms_teams_meeting_id ||
        !!this.invite?.ms_teams_join_meeting_id;
      const mode = String(
        this.reportRecord?.interviewer_brief?.interview_mode ||
          this.reportRecord?.interview_mode ||
          ""
      ).trim();
      return [
        { label: "Format", value: "Person Interview" },
        {
          label: "Duration",
          value:
            Number.isFinite(durationMinutes) && durationMinutes > 0
              ? `${durationMinutes} Minutes`
              : scheduledValue
        },
        { label: "Interview Mode", value: mode || (hasTeamsLink ? "Microsoft Teams" : "N/A") }
      ];
    },
    insights(): { label: string; value: number }[] {
      const scores = this.conductScores || {};
      return Object.keys(scores).map((key) => ({
        label: this.formatLabel(key),
        value: Number(scores[key] || 0)
      }));
    },
    radarValues(): number[] {
      const getScore = (key: string) => Number((this.conductScores || {})[key] || 0);
      return [
        getScore("guideline_adherence"),
        getScore("probe_followthrough"),
        getScore("bias_neutral_language"),
        getScore("competency_based_framing"),
        Number(this.overallScore || 0)
      ].map((v) => Math.min(100, Math.max(0, Number(v || 0))));
    },
    radarAxisLines(): Array<{ x1: number; y1: number; x2: number; y2: number }> {
      const cx = 130;
      const cy = 130;
      const radius = 90;
      return [0, 1, 2, 3, 4].map((i) => {
        const angle = (-90 + i * 72) * (Math.PI / 180);
        return {
          x1: cx,
          y1: cy,
          x2: cx + radius * Math.cos(angle),
          y2: cy + radius * Math.sin(angle)
        };
      });
    },
    radarRings(): string[] {
      const cx = 130;
      const cy = 130;
      const radius = 90;
      const rings = [0.25, 0.5, 0.75, 1];
      return rings.map((ratio) =>
        [0, 1, 2, 3, 4]
          .map((i) => {
            const angle = (-90 + i * 72) * (Math.PI / 180);
            const x = cx + radius * ratio * Math.cos(angle);
            const y = cy + radius * ratio * Math.sin(angle);
            return `${x},${y}`;
          })
          .join(" ")
      );
    },
    radarValuePolygon(): string {
      const cx = 130;
      const cy = 130;
      const radius = 90;
      return this.radarValues
        .map((value, i) => {
          const ratio = value / 100;
          const angle = (-90 + i * 72) * (Math.PI / 180);
          const x = cx + radius * ratio * Math.cos(angle);
          const y = cy + radius * ratio * Math.sin(angle);
          return `${x},${y}`;
        })
        .join(" ");
    },
    finalRadarValues(): number[] {
      const scores = this.finalReport?.scores || {};
      return [
        Number(scores?.communication || 0),
        Number(scores?.technical || scores?.technical_competency || 0),
        Number(scores?.behavioral || 0),
        Number(scores?.cultural_fit || 0),
        Number(this.finalReport?.overall_score || 0)
      ].map((v) => Math.min(100, Math.max(0, Number(v || 0))));
    },
    finalRadarValuePolygon(): string {
      const cx = 130;
      const cy = 130;
      const radius = 90;
      return this.finalRadarValues
        .map((value, i) => {
          const ratio = value / 100;
          const angle = (-90 + i * 72) * (Math.PI / 180);
          const x = cx + radius * ratio * Math.cos(angle);
          const y = cy + radius * ratio * Math.sin(angle);
          return `${x},${y}`;
        })
        .join(" ");
    },
    canGenerateFinalReport(): boolean {
      return (
        !this.generatingFinalReport &&
        Object.keys(this.auditReport || {}).length > 0 &&
        (!this.finalReport || !Object.keys(this.finalReport).length)
      );
    },
    finalRecommendationText(): string {
      return this.finalReport?.recommendation || "-";
    },
    finalOverallScore(): string {
      const value = Number(this.finalReport?.overall_score || 0);
      return value > 0 ? String(value) : "-";
    },
    finalConfidence(): string {
      return this.finalReport?.confidence || "-";
    },
    finalSummary(): string {
      return this.finalReport?.executive_summary || "";
    },
    isFinalView(): boolean {
      return String(this.$route.query.view || "person").toLowerCase() === "final";
    },
    finalScoresList(): Array<{ label: string; value: number }> {
      const scores = this.finalReport?.scores || {};
      return Object.keys(scores).map((key) => ({
        label: this.formatLabel(key),
        value: Number(scores[key] || 0)
      }));
    },
    finalConsistencyList(): any[] {
      return Array.isArray(this.finalReport?.cross_interview_consistency)
        ? this.finalReport.cross_interview_consistency
        : [];
    },
    finalStrengthsList(): any[] {
      return Array.isArray(this.finalReport?.strengths) ? this.finalReport.strengths : [];
    },
    finalDevelopmentList(): any[] {
      return Array.isArray(this.finalReport?.development_areas)
        ? this.finalReport.development_areas
        : [];
    },
    finalMandatoryProbesList(): any[] {
      return Array.isArray(this.finalReport?.mandatory_next_probes)
        ? this.finalReport.mandatory_next_probes
        : [];
    },
    hiringDecisionSummaryRows(): Array<{ label: string; value: string }> {
      const summary = this.finalReport?.hiring_decision_summary || {};
      return [
        { label: "Decision", value: String(summary?.decision || "-") },
        { label: "Primary reason", value: String(summary?.primary_reason || "-") },
        { label: "Biggest risk", value: String(summary?.biggest_risk || "-") },
        { label: "Biggest strength", value: String(summary?.biggest_strength || "-") }
      ];
    },
    candidateProfileRows(): Array<{ label: string; value: string }> {
      const profile = this.finalReport?.candidate_profile || {};
      return [
        { label: "Experience match", value: String(profile?.experience_match || "-") },
        { label: "Education match", value: String(profile?.education_match || "-") },
        { label: "Notice period", value: String(profile?.notice_period || "-") },
        { label: "Salary expectation", value: String(profile?.salary_expectation || "-") }
      ];
    },
    candidateSkillAlignmentList(): any[] {
      return Array.isArray(this.finalReport?.candidate_profile?.skill_alignment)
        ? this.finalReport.candidate_profile.skill_alignment
        : [];
    },
    candidateResumeGapsList(): string[] {
      const items = this.finalReport?.candidate_profile?.resume_vs_jd_gaps;
      return Array.isArray(items)
        ? items.map((item: any) => String(item || "").trim()).filter((item: string) => !!item)
        : [];
    },
    competencyScoresList(): any[] {
      return Array.isArray(this.finalReport?.competency_scores)
        ? this.finalReport.competency_scores
        : [];
    },
    communicationAnalysisRows(): Array<{ label: string; value: string }> {
      const analysis = this.finalReport?.communication_analysis || {};
      const rows = [
        "clarity",
        "vocabulary",
        "conciseness",
        "structure",
        "active_listening",
        "confidence",
        "overall_communication_score"
      ]
        .filter((key: string) => analysis[key] !== undefined && analysis[key] !== null)
        .map((key: string) => ({
          label: this.formatLabel(key),
          value: String(analysis[key])
        }));
      return rows;
    },
    communicationAnalysisSummary(): string {
      return String(this.finalReport?.communication_analysis?.summary || "");
    },
    behavioralAnalysisRows(): Array<{ label: string; value: string }> {
      const analysis = this.finalReport?.behavioral_analysis || {};
      const keys = [
        "ownership_mindset",
        "resilience_under_pressure",
        "conflict_handling",
        "growth_mindset",
        "adaptability"
      ];
      return keys
        .filter((key: string) => analysis[key] !== undefined && analysis[key] !== null)
        .map((key: string) => ({
          label: this.formatLabel(key),
          value: String(analysis[key])
        }));
    },
    behavioralObservedPatternsList(): any[] {
      return Array.isArray(this.finalReport?.behavioral_analysis?.observed_patterns)
        ? this.finalReport.behavioral_analysis.observed_patterns
        : [];
    },
    technicalAssessmentRows(): Array<{ label: string; value: string }> {
      const technical = this.finalReport?.technical_assessment || {};
      const rows: Array<{ label: string; value: string }> = [];
      if (technical?.overall_technical_score !== undefined) {
        rows.push({
          label: "Overall technical score",
          value: String(technical.overall_technical_score)
        });
      }
      if (technical?.depth_rating !== undefined) {
        rows.push({ label: "Depth rating", value: String(technical.depth_rating || "-") });
      }
      if (technical?.technical_summary) {
        rows.push({ label: "Technical summary", value: String(technical.technical_summary) });
      }
      return rows;
    },
    technicalPerSkillList(): any[] {
      return Array.isArray(this.finalReport?.technical_assessment?.per_skill_scores)
        ? this.finalReport.technical_assessment.per_skill_scores
        : [];
    },
    interviewPerformanceRows(): Array<{ label: string; value: string }> {
      const perf = this.finalReport?.interview_performance_comparison || {};
      return [
        { label: "AI interview score", value: String(perf?.ai_interview_score ?? "-") },
        { label: "Human interview score", value: String(perf?.human_interview_score ?? "-") },
        { label: "Performance trend", value: String(perf?.performance_trend || "-") },
        { label: "Trend explanation", value: String(perf?.trend_explanation || "-") }
      ];
    },
    riskAssessmentRows(): Array<{ label: string; value: string }> {
      const risk = this.finalReport?.risk_assessment || {};
      return [{ label: "Overall risk", value: String(risk?.overall_risk || "-") }];
    },
    riskAssessmentRisksList(): any[] {
      return Array.isArray(this.finalReport?.risk_assessment?.risks)
        ? this.finalReport.risk_assessment.risks
        : [];
    },
    onboardingRecommendationsList(): string[] {
      return Array.isArray(this.finalReport?.onboarding_recommendations)
        ? this.finalReport.onboarding_recommendations
            .map((item: any) => this.formatOnboardingRecommendation(item))
            .filter((item: string) => !!item)
        : [];
    }
  },
  async created() {
    await this.loadInterviewContext();
    await this.loadOrGenerateAuditReport();
  },
  methods: {
    ...mapActions("auth", {
      fetchUserById: USER_DETAILS_BY_ID
    }),
    ...mapActions("recruiter_ai_interviews", {
      initInvites: INIT_AI_INTERVIEW_INVITES
    }),
    async loadInterviewContext() {
      await this.initInvites();
      if (!this.resolvedCandidateId) return;
      try {
        this.candidateUser = await this.fetchUserById(this.resolvedCandidateId);
      } catch (_e) {
        this.candidateUser = null;
      }
    },
    formatScheduleValue(value: string): string {
      if (!value) return "Scheduled";
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return "Scheduled";
      return parsed.toLocaleString();
    },
    formatLabel(key: string): string {
      return key
        .split("_")
        .map((k) => k.charAt(0).toUpperCase() + k.slice(1))
        .join(" ");
    },
    formatOnboardingRecommendation(item: any): string {
      if (typeof item === "string") return item.trim();
      if (!item || typeof item !== "object") return String(item || "").trim();

      const orderedKeys = [
        "recommendation",
        "title",
        "action",
        "step",
        "focus_area",
        "area",
        "description",
        "details",
        "note"
      ];
      for (const key of orderedKeys) {
        const value = String(item?.[key] || "").trim();
        if (value) return value;
      }

      const textParts = Object.entries(item)
        .map(([key, value]) => {
          const raw = String(value ?? "").trim();
          if (!raw || raw === "[object Object]") return "";
          return `${this.formatLabel(key)}: ${raw}`;
        })
        .filter((part) => !!part);
      if (textParts.length) return textParts.join(" | ");

      return "";
    },
    async loadOrGenerateAuditReport() {
      const candidateId = Number(this.resolvedCandidateId || 0);
      const scopedJobId = Number(this.routeJobId || this.invite?.job_id || 0) || null;
      if (!candidateId) return;
      const resp = await get_latest_interview_report(candidateId, "person", scopedJobId);
      this.reportRecord = resp?.data?.data || null;
      if (this.reportRecord?.audit_report) return;

      const interviewerBrief = this.reportRecord?.interviewer_brief || {};
      const humanTranscript = this.reportRecord?.human_transcript || [];
      let aiReport = this.reportRecord?.ai_report || {};
      if (!Object.keys(aiReport).length) {
        const aiResp = await get_latest_interview_report(candidateId, "ai", scopedJobId);
        aiReport = aiResp?.data?.data?.ai_report || {};
      }
      if (
        !Object.keys(interviewerBrief).length ||
        !Array.isArray(humanTranscript) ||
        !humanTranscript.length ||
        !Object.keys(aiReport).length
      ) {
        return;
      }
      const auditResp = await generate_audit({
        interviewer_brief: interviewerBrief,
        human_transcript: humanTranscript,
        ai_report: aiReport
      });
      const auditReport =
        auditResp?.data?.data?.audit_report || auditResp?.data?.audit_report || {};
      if (!Object.keys(auditReport).length) return;
      await upsert_interview_report({
        candidate_id: candidateId,
        interview_type: "person",
        job_id: scopedJobId,
        thread_id: this.reportRecord?.thread_id || null,
        question_bank: this.reportRecord?.question_bank || [],
        ai_report: aiReport,
        interviewer_brief: interviewerBrief,
        human_transcript: humanTranscript,
        audit_report: auditReport,
        status: "completed"
      });
      const latest = await get_latest_interview_report(candidateId, "person", scopedJobId);
      this.reportRecord = latest?.data?.data || this.reportRecord;
    },
    async generateFinalReportNow() {
      if (!this.canGenerateFinalReport) return;
      const candidateId = Number(this.$route.query.candidateId || 0);
      const scopedJobId = Number(this.$route.query.jobId || 0) || null;
      if (!candidateId) return;
      this.generatingFinalReport = true;
      try {
        const [personResp, aiResp] = await Promise.all([
          get_latest_interview_report(candidateId, "person", scopedJobId),
          get_latest_interview_report(candidateId, "ai", scopedJobId)
        ]);
        const person = personResp?.data?.data || {};
        const ai = aiResp?.data?.data || {};
        const aiReport = person?.ai_report || ai?.ai_report || {};
        const auditReport = person?.audit_report || {};
        const aiTranscript = ai?.ai_transcript || [];
        const humanTranscript = person?.human_transcript || [];
        if (
          !Object.keys(aiReport).length ||
          !Object.keys(auditReport).length ||
          !Array.isArray(humanTranscript) ||
          !humanTranscript.length
        ) {
          return;
        }
        let resumeText = "Candidate resume";
        try {
          const profileResp = await fetch_user_with_profile(candidateId);
          const profilePayload = profileResp?.data?.data || profileResp?.data || {};
          resumeText =
            JSON.stringify(profilePayload?.profile || profilePayload || {}) || "Candidate resume";
        } catch (_e) {
          // ignore and keep fallback
        }
        const jobId = Number(person?.job_id || ai?.job_id || 0);
        let jdText =
          String(person?.job_description || "").trim() ||
          String(person?.job_role || "").trim() ||
          String(ai?.job_description || "").trim() ||
          String(ai?.job_role || "").trim() ||
          "Interview role";
        if (jobId) {
          try {
            const jobResp = await get_job_by_id(jobId);
            const jobPayload = jobResp?.data?.data || jobResp?.data || {};
            const jdFromJob = String(
              jobPayload?.job_description || jobPayload?.description || jobPayload?.jd_text || ""
            ).trim();
            if (jdFromJob) jdText = jdFromJob;
          } catch (_e) {
            // ignore and keep fallback
          }
        }
        const finalResp = await generate_final_report({
          resume_text: resumeText,
          jd_text: jdText,
          pre_interview_form: {},
          ai_report: aiReport,
          audit_report: auditReport,
          ai_transcript: Array.isArray(aiTranscript) ? aiTranscript : [],
          human_transcript: humanTranscript
        });
        const finalReport =
          finalResp?.data?.data?.final_report || finalResp?.data?.final_report || {};
        if (!Object.keys(finalReport).length) return;
        await upsert_interview_report({
          candidate_id: candidateId,
          interview_type: "person",
          job_id: scopedJobId,
          ai_report: aiReport,
          interviewer_brief: person?.interviewer_brief || {},
          human_transcript: humanTranscript,
          audit_report: auditReport,
          final_report: finalReport,
          status: "completed"
        });
        const latest = await get_latest_interview_report(candidateId, "person", scopedJobId);
        this.reportRecord = latest?.data?.data || this.reportRecord;
      } finally {
        this.generatingFinalReport = false;
      }
    }
  }
});
